import * as React from 'react'
import { Box } from '@mui/material'
import KpiResults from '@/components/DatePickers/KpiResults'
import YearComparisonChart from '@/components/charts/YearComparisonChart'
import DateRangeFilterCard from '@/components/common/DateRangeFilterCard'
import type { FilterToolbarField } from '@/components/common/FilterToolbar'
import { getDefaultFifteenthDateRange } from '@/utils/consumerReadingsDateRanges'

export type DateRange = { from: Date | null; to: Date | null }

type KPI = {
  label: string
  value: string | number
  sublabel?: string
}

import type { PeriodOption } from '@/services/consumerService'

type Props = {
  range?: DateRange
  onRangeChange?: (r: DateRange) => void
  onSearch?: (from?: string, to?: string, periodOption?: PeriodOption) => void
  kpis?: KPI[]
  disabled?: boolean
  loading?: boolean
  onPeriodsChange?: (periods: string) => void
  data?: any[]
  totalConsumption?: number
  avgConsumption?: number
  chartData?: { currentYear: number[]; yearAgo: number[] | null }
}

type PeriodType =
  | 'monthly'
  | 'bi-monthly'
  | 'quarterly'
  | 'half-year'
  | 'yearly'

// Map UI period type to API PeriodOption
const mapPeriodToApiOption = (period: PeriodType): PeriodOption => {
  switch (period) {
    case 'monthly':
      return 'MONTHLY'
    case 'bi-monthly':
      return 'BI_MONTHLY'
    case 'quarterly':
      return 'QUARTERLY'
    case 'half-year':
      return 'SEMI_ANNUAL'
    case 'yearly':
      return 'ANNUAL'
    default:
      return 'MONTHLY'
  }
}

const formatDateForApi = (date: Date) => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const getPeriodStart = (row: any) => {
  const rawValue =
    row?.periodStart ?? row?.startTimestamp ?? row?.period ?? row?.startDate
  const match = String(rawValue ?? '').match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (!match) return null

  const date = new Date(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3])
  )
  return Number.isNaN(date.getTime()) ? null : date
}

const periodMonths: Record<PeriodType, number> = {
  monthly: 1,
  'bi-monthly': 2,
  quarterly: 3,
  'half-year': 6,
  yearly: 12,
}

const getPeriodEnd = (start: Date, period: PeriodType) => {
  const end = new Date(start)
  end.setMonth(end.getMonth() + periodMonths[period])
  end.setDate(end.getDate() - 1)
  return end
}

const getTodayStart = () => {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return today
}

const getPeriodLabel = (start: Date, end: Date, period: PeriodType) => {
  if (period === 'yearly') return String(start.getFullYear())

  const size = periodMonths[period]
  const endMonth = end.getMonth() + 1
  const startMonth = ((endMonth - size + 12) % 12) + 1
  return size === 1 ? String(endMonth) : `${startMonth}-${endMonth}`
}

export default function PeriodicFilter({
  range: rangeProp,
  onRangeChange,
  onSearch,
  disabled,
  loading,
  onPeriodsChange,
  data,
  totalConsumption,
  avgConsumption,
  chartData,
}: Props) {
  const getDefaultRangeForFilter = (): DateRange => getDefaultFifteenthDateRange(1)
  const [innerRange, setInnerRange] =
    React.useState<DateRange>(getDefaultRangeForFilter())
  const isControlled = rangeProp !== undefined
  const currRange: DateRange = isControlled
    ? (rangeProp as DateRange)
    : innerRange

  const applyRange = React.useCallback(
    (next: DateRange) => {
      onRangeChange?.(next)
      if (!isControlled) {
        setInnerRange(next)
      }
    },
    [onRangeChange, isControlled]
  )

  const { from, to } = currRange

  const formatForField = (d: Date | null) => {
    if (!d) return ''
    const dd = String(d.getDate()).padStart(2, '0')
    const mm = String(d.getMonth() + 1).padStart(2, '0')
    const yyyy = d.getFullYear()
    return `${dd}/${mm}/${yyyy}`
  }

  const handleSearch = () => {
    // Allow retrying the same range, especially after an empty or failed API response.
    if (onPeriodsChange) {
      onPeriodsChange(period)
    }
    if (onSearch && from && to && from <= to) {
      onSearch(
        formatDateForApi(from),
        formatDateForApi(to),
        mapPeriodToApiOption(period)
      )
      return
    }
  }

  // ----- Period select (same width as first ClickableDateField) -----
  const [period, setPeriod] = React.useState<PeriodType>('bi-monthly')

  const rowComparison = React.useMemo(() => {
    const currentYear = currRange.to?.getFullYear() ?? new Date().getFullYear()
    const parsedRows = (data ?? [])
      .map(row => {
        const start = getPeriodStart(row)
        const consumption = Number(row?.consumption)
        if (!start || !Number.isFinite(consumption)) return null

        const end = getPeriodEnd(start, period)
        return {
          year: start.getFullYear(),
          periodKey: period === 'yearly' ? 'year' : String(start.getMonth()),
          end,
          consumption,
          label: getPeriodLabel(start, end, period),
        }
      })
      .filter((row): row is NonNullable<typeof row> => row !== null)
      // Do not plot a period while it is still in progress. An incomplete
      // period is often returned with a zero/partial consumption value and
      // would incorrectly make the chart drop to zero.
      .filter(row => row.end < getTodayStart())
      .sort((left, right) => left.end.getTime() - right.end.getTime())

    const buildSeries = (year: number) =>
      parsedRows.filter(row => row.year === year)

    const currentRows = buildSeries(currentYear)
    const previousRows = buildSeries(currentYear - 1)
    const consumptionByPeriod = new Map(
      parsedRows.map(row => [
        `${row.year}-${row.periodKey}`,
        row.consumption,
      ])
    )
    const allPreviousData = parsedRows.map(row =>
      consumptionByPeriod.get(`${row.year - 1}-${row.periodKey}`)
    )

    const hasMultipleYears = new Set(parsedRows.map(row => row.year)).size > 2

    return {
      allData: parsedRows.map(row => row.consumption),
      allPreviousData,
      // A period number repeats every year. Include the year when the full
      // timeline is shown so every tick remains unambiguous.
      allLabels: parsedRows.map(row =>
        hasMultipleYears ? `${row.year} · ${row.label}` : row.label
      ),
      hasMoreThanTwoYears: hasMultipleYears,
      currentData: currentRows.map(row => row.consumption),
      previousData: previousRows.map(row => row.consumption),
      labels: (currentRows.length ? currentRows : previousRows).map(
        row => row.label
      ),
    }
  }, [currRange.to, data, period])

  const effectiveChartData = React.useMemo(() => {
    // Keep the complete timeline and align each point with the same period
    // from the previous year so comparison remains available.
    if (rowComparison.hasMoreThanTwoYears) {
      return {
        currentData: rowComparison.allData,
        previousData: rowComparison.allPreviousData,
        labels: rowComparison.allLabels,
      }
    }

    // `data` is the authoritative period list. If it exists, never fall back
    // to the API chart arrays because those arrays may still contain the
    // currently open period.
    if (data?.length) {
      return rowComparison
    }

    // Period rows are the most reliable source when both years are already
    // present in the table but the API omitted its optional yearAgo series.
    if (rowComparison.currentData.length) {
      return rowComparison
    }

    if (chartData?.currentYear?.length) {
      return {
        currentData: chartData.currentYear,
        previousData: chartData.yearAgo?.length
          ? chartData.yearAgo
          : rowComparison.previousData,
        labels: rowComparison.labels,
      }
    }

    return {
      currentData: rowComparison.currentData,
      previousData: rowComparison.previousData,
      labels: rowComparison.labels,
    }
  }, [chartData, data, rowComparison])

  const periodLabels = React.useMemo(() => {
    const count = Math.max(
      effectiveChartData.currentData.length,
      effectiveChartData.previousData.length
    )
    if (effectiveChartData.labels.length === count) {
      return effectiveChartData.labels
    }

    const size = periodMonths[period]
    const formatPeriodLabel = (startDate: Date) => {
      if (period === 'yearly') return String(startDate.getFullYear())

      const startMonth = startDate.getMonth() + 1
      const endMonth = ((startDate.getMonth() + size - 1) % 12) + 1
      return size === 1 ? String(startMonth) : `${startMonth}-${endMonth}`
    }

    const dataPeriodStarts = (data ?? [])
      .map(getPeriodStart)
      .filter((date): date is Date => date !== null)
      .filter(date => getPeriodEnd(date, period) < getTodayStart())
      .sort((left, right) => left.getTime() - right.getTime())

    if (dataPeriodStarts.length === count) {
      return dataPeriodStarts.map(formatPeriodLabel)
    }

    if (period === 'yearly') {
      const endYear = currRange.to?.getFullYear() ?? new Date().getFullYear()
      return Array.from({ length: count }, (_, index) =>
        String(endYear - count + index + 1)
      )
    }

    return Array.from({ length: count }, (_, index) => {
      const start = ((index * size) % 12) + 1
      const end = Math.min(start + size - 1, 12)
      return size === 1 ? String(start) : `${start}-${end}`
    })
  }, [data, effectiveChartData, period, currRange.to])

  return (
    <Box
      dir="rtl"
      sx={{ display: 'flex', flexDirection: 'column', gap: 2, ml: '10px' }}
    >
      <DateRangeFilterCard
        range={currRange}
        onRangeChange={applyRange}
        onSearch={() => handleSearch()}
        loading={loading}
        disabled={disabled}
        extraFields={[
          {
            name: 'period',
            type: 'select',
            label: 'סוג התקופה',
            value: period,
            width: 'clamp(150px, 10vw, 190px)',
            options: [
              { value: 'monthly', label: 'חודשי' },
              { value: 'bi-monthly', label: 'דו-חודשי' },
              { value: 'quarterly', label: 'רבעוני' },
              { value: 'half-year', label: 'חצי שנתי' },
              { value: 'yearly', label: 'שנתי' },
            ],
            onChange: value => setPeriod(value as PeriodType),
          } satisfies FilterToolbarField,
        ]}
        compact
      />

      {(loading || effectiveChartData.currentData.length > 0) && (
        <>
          <YearComparisonChart
            legend2={false}
            showFooter={false}
            loading={loading}
            comparedData={effectiveChartData}
            periodLabels={periodLabels}
            seriesLabels={
              rowComparison.hasMoreThanTwoYears
                ? { curr: 'כל התקופות', prev: 'שנה קודמת' }
                : undefined
            }
            year={currRange.to?.getFullYear() ?? new Date().getFullYear()}
            periodicData={true}
          />
          <KpiResults
            isSame={true}
            totalM3={totalConsumption ?? 0}
            hourlyAvgM3={avgConsumption ?? 0}
            firstDateRangeText="השנה הקודמת"
            secondDateRangeText={`${formatForField(from)} - ${formatForField(to)}`}
          />
        </>
      )}
    </Box>
  )
}
